import { Box, Grid, Typography } from "@mui/material"
import { useMemo } from "react"
import { makeStyles } from "tss-react/mui"

import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardMainPanel } from "../../../../../../shared/components/forms/StandardMainPanel"
import { StandardForm } from "../../../../../../shared/components/forms/StandardForm"
import { StandardSidePanel } from "../../../../../../shared/components/forms/StandardSidePanel"
import { StandardAlertInfo } from "../../../../../../shared/components/alert/StandardAlertInfo"
import { FormikPinField, FormikSwitch, FormikTextField } from "../../../../../../shared/components/forms/Formik"
import { TenantAdminRolesField } from "../../../components/TenantAdminRolesField"
import { UserType } from "../../../../../login/loginTypes"
import { selectUserId } from "../../../../../login/loginSelectors"
import { useAppSelector } from "../../../../../store/storeHooks"
import { Admin, AdminRole } from "../../../../../users/userTypes"
import { useDateTime } from "../../../../../localization/useDateTime"

import { CreateEditTenantAdminFormValues } from "../tenantAdminTypes"

const useStyles = makeStyles()(() => ({
    pinFieldContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
    },
}))

type CreateUpdateTenantAdminContentProps = Readonly<{
    adminType: Admin["type"]
    mode: "create" | "update"
    existingAdmin?: Admin
}>

export function CreateUpdateTenantAdminContent({
    adminType,
    mode,
    existingAdmin,
}: CreateUpdateTenantAdminContentProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const currentUserId = useAppSelector(selectUserId)
    const isOwnProfile = mode === "update" && existingAdmin?.user.id === currentUserId
    const isMerchantOrSupplier = adminType === UserType.MERCHANT || adminType === UserType.SUPPLIER
    const showPinField = isOwnProfile && isMerchantOrSupplier

    const excludeRoles = useMemo(() => {
        if (adminType === UserType.SERVICE_CENTER) {
            return [AdminRole.agreement_owner, AdminRole.dpo, AdminRole.bookkeeper]
        }
        return adminType === UserType.MERCHANT || adminType === UserType.SUPPLIER ? [AdminRole.bookkeeper] : undefined
    }, [])

    return (
        <StandardForm>
            <StandardMainPanel>
                <LocalizedStrict id="office-manager-create-user-field-name" attrs={{ label: true }}>
                    <FormikTextField<CreateEditTenantAdminFormValues>
                        required
                        name="displayName"
                        label="Name"
                        autoComplete="name"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-email" attrs={{ label: true }}>
                    <FormikTextField<CreateEditTenantAdminFormValues>
                        required
                        name="email"
                        label="Email"
                        autoComplete="email"
                        fullWidth
                    />
                </LocalizedStrict>
                <LocalizedStrict id="office-manager-create-user-field-mobile" attrs={{ label: true }}>
                    <FormikTextField<CreateEditTenantAdminFormValues>
                        name="mobileNumber"
                        label="Mobile"
                        autoComplete="mobile"
                        fullWidth
                    />
                </LocalizedStrict>
                <TenantAdminRolesField name="roles" excludeRoles={excludeRoles} />
            </StandardMainPanel>
            {mode === "update" && (
                <LocalizedStrict id="office-manager-settings-panel" attrs={{ title: true }}>
                    <StandardSidePanel title="Settings">
                        <LocalizedStrict id="office-manager-create-user-field-active">
                            <FormikSwitch<CreateEditTenantAdminFormValues> name="isActive">Active</FormikSwitch>
                        </LocalizedStrict>
                    </StandardSidePanel>
                </LocalizedStrict>
            )}

            <LocalizedStrict id="office-manager-security-panel" attrs={{ title: true }}>
                <StandardSidePanel title="Security">
                    <LocalizedStrict id="office-manager-security-manage-admins">
                        <FormikSwitch<CreateEditTenantAdminFormValues> name="allowToCreateAdmins">
                            Can manage other admins
                        </FormikSwitch>
                    </LocalizedStrict>
                    {showPinField && (
                        <Box className={classes.pinFieldContainer}>
                            <LocalizedStrict id="office-manager-security-pin-field" attrs={{ label: true }}>
                                <FormikPinField<CreateEditTenantAdminFormValues> name="pinCode" label="PIN" fullWidth />
                            </LocalizedStrict>
                            <LocalizedStrict id="office-manager-security-pin-info" attrs={{ title: true }}>
                                <StandardAlertInfo
                                    title="PIN is used by staff members to sign into POS screen."
                                    severity="info"
                                />
                            </LocalizedStrict>
                        </Box>
                    )}
                </StandardSidePanel>
            </LocalizedStrict>

            {existingAdmin && existingAdmin.history !== undefined && (
                <LocalizedStrict id="history-card" attrs={{ title: true }}>
                    <StandardSidePanel title="history">
                        <Grid container rowGap={3}>
                            <Grid container item>
                                <Grid item xs={12} sm={5} justifyContent="space-between">
                                    <LocalizedStrict id="history-created">
                                        <Typography variant="body2">Created:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {dateTimeFactory.formatDateTime(
                                            dateTimeFactory.fromApi(existingAdmin.history.created.timestamp),
                                        )}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right" fontWeight="bold">
                                        {existingAdmin.history.created.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid container item>
                                <Grid item xs={12} sm={5}>
                                    <LocalizedStrict id="history-edited">
                                        <Typography variant="body2">Edited:</Typography>
                                    </LocalizedStrict>
                                </Grid>
                                <Grid item xs={12} sm={7}>
                                    <Typography variant="body2" textAlign="right">
                                        {existingAdmin.history.updated
                                            ? dateTimeFactory.formatDateTime(
                                                  dateTimeFactory.fromApi(existingAdmin.history.updated.timestamp),
                                              )
                                            : "-"}
                                    </Typography>
                                    <Typography variant="body2" textAlign="right" fontWeight="bold">
                                        {existingAdmin.history.updated?.author ?? "-"}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </StandardSidePanel>
                </LocalizedStrict>
            )}
        </StandardForm>
    )
}
